import { useEffect, useState } from 'react';
import { Tooltip } from 'ui/tooltip';
import { Container, Description, GroupLabel, Label, RGIndicator, RGItem, RGRoot } from './Styled';
import { Orientation, Props } from './types';

export const RADIO_LIST_PREFIX = 'radio-list-';
export const RADIO_LIST_OPTION_PREFIX = 'radio-list-option-';

export const RadioGroup = ({
  name: groupName,
  label: groupLabel,
  options = [],
  value,
  onSelect,
  orientation = Orientation.VERTICAL,
  isReadOnly = false,
  isBordered = false,
  radioAlignment = 'center',
  alignRight = false,
  isOverlapped = false,
  ...props
}: Props) => {
  const [selectedValue, selectValue] = useState(options[0]?.value);
  useEffect(() => {
    // This keeps internal state in sync, if parent controls selected value
    if (value) {
      selectValue(value);
    }
  }, [value]);

  const onClick = (val: string) => {
    selectValue(val);
    // Trigger parent callback, if present
    if (onSelect) {
      onSelect(val);
    }
  };
  return (
    <RGRoot
      orientation={orientation}
      className="RGRoot"
      defaultValue="default"
      value={selectedValue}
      aria-label={groupName}
      onValueChange={onClick}
      data-test={`${RADIO_LIST_PREFIX}${groupName}`}
      {...props}
    >
      {groupLabel && <GroupLabel id={groupName}>{groupLabel}</GroupLabel>}
      {options.map(
        ({
          id,
          isDisabled,
          description,
          label,
          value: val,
          isTooltipEnabled = false,
          tooltipContent,
        }) => (
          <Tooltip key={id} isEnabled={isTooltipEnabled} tooltipContent={tooltipContent}>
            <Container
              className="RGItemContainer"
              key={id}
              $isBordered={isBordered}
              $isDisabled={!!(isDisabled || isReadOnly)}
              $isSelected={val === selectedValue}
              onClick={() => isBordered && !isDisabled && !isReadOnly && onClick(val)}
              $alignItems={radioAlignment}
              $alignRight={alignRight}
              data-disabled={!!(isDisabled || isReadOnly)}
            >
              <RGItem
                data-test={`${RADIO_LIST_OPTION_PREFIX}${id}`}
                disabled={isDisabled || isReadOnly}
                className="RGItem"
                $isBordered={isBordered}
                value={val}
                id={id}
              >
                <RGIndicator className="RGIndicator" />
              </RGItem>
              <Label
                disabled={isDisabled || isReadOnly}
                className="Label"
                htmlFor={id}
                overlap={isOverlapped}
              >
                {label}
                {description && (
                  <Description disabled={isDisabled || isReadOnly} centered={isOverlapped}>
                    {description}
                  </Description>
                )}
              </Label>
            </Container>
          </Tooltip>
        ),
      )}
    </RGRoot>
  );
};
